import React, { useState, useEffect } from 'react';
import { Card, Progress, Typography, Space, Tooltip, Spin } from 'antd';
import { InfoCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/apiService';

const { Text, Title } = Typography;

const ComplianceUsageWidget = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { getAuthToken } = useAuth();

  const loadUsage = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getAuthToken();
      const response = await apiService.getComplianceUsage(token);
      setUsage(response.usage);
    } catch (error) {
      console.error('Failed to load compliance usage:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsage();
    
    // Refresh usage every 5 minutes
    const interval = setInterval(loadUsage, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Card 
        size="small" 
        style={{ 
          background: 'var(--bg-secondary)', 
          border: '1px solid var(--border-secondary)',
          marginBottom: '16px'
        }}
      >
        <div style={{ textAlign: 'center', padding: '12px' }}>
          <Spin size="small" />
          <Text style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>
            Loading usage...
          </Text>
        </div>
      </Card>
    );
  }

  if (error || !usage) {
    return (
      <Card 
        size="small" 
        style={{ 
          background: 'var(--bg-secondary)', 
          border: '1px solid var(--border-secondary)',
          marginBottom: '16px'
        }}
      >
        <div style={{ textAlign: 'center', padding: '8px' }}>
          <Text style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
            Usage info unavailable
          </Text>
        </div>
      </Card>
    );
  }

  if (!usage.has_subscription) {
    return (
      <Card 
        size="small" 
        style={{ 
          background: 'var(--bg-secondary)', 
          border: '1px solid var(--border-secondary)',
          marginBottom: '16px'
        }}
      >
        <div style={{ textAlign: 'center', padding: '8px' }}>
          <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: '6px' }} />
          <Text style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
            No active subscription
          </Text>
        </div>
      </Card>
    );
  }

  const getStatusColor = () => {
    if (usage.is_unlimited || usage.usage_percentage < 80) return '#52c41a';
    if (usage.usage_percentage < 95) return '#faad14';
    return '#ff4d4f';
  };

  const getStatusIcon = () => {
    if (usage.is_unlimited) return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    if (usage.usage_percentage < 80) return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    if (usage.usage_percentage < 95) return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
    return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
  };

  const formatBillingPeriod = () => {
    if (!usage.billing_period_end) return '';
    const endDate = new Date(usage.billing_period_end);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Period ended';
    if (diffDays === 1) return 'Resets tomorrow';
    if (diffDays <= 7) return `Resets in ${diffDays} days`;
    return `Resets ${endDate.toLocaleDateString()}`;
  };

  return (
    <Card 
      size="small" 
      style={{ 
        background: 'var(--bg-secondary)', 
        border: '1px solid var(--border-secondary)',
        marginBottom: '16px'
      }}
    >
      <div style={{ padding: '4px 0' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <Text 
            strong 
            style={{ 
              fontSize: '12px', 
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            Usage
          </Text>
          <Tooltip title={usage.plan_name || 'Compliance Plan'}>
            {getStatusIcon()}
          </Tooltip>
        </div>

        {usage.is_unlimited ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px', marginBottom: '4px' }} />
            <div>
              <Text style={{ color: 'var(--text-primary)', fontSize: '11px', display: 'block' }}>
                Unlimited Usage
              </Text>
              <Text style={{ color: 'var(--text-secondary)', fontSize: '10px' }}>
                {usage.current_usage} requests this period
              </Text>
            </div>
          </div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Progress
              percent={usage.usage_percentage}
              size="small"
              strokeColor={getStatusColor()}
              trailColor="var(--border-secondary)"
              showInfo={false}
              style={{ margin: '0' }}
            />
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <Text style={{ fontSize: '11px', color: 'var(--text-primary)' }}>
                {usage.current_usage.toLocaleString()} / {usage.limit.toLocaleString()}
              </Text>
              <Text style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                {usage.usage_percentage}%
              </Text>
            </div>
            
            {usage.remaining > 0 && (
              <Text style={{ fontSize: '10px', color: 'var(--text-secondary)', display: 'block' }}>
                {usage.remaining.toLocaleString()} remaining
              </Text>
            )}
            
            {usage.billing_period_end && (
              <Text style={{ fontSize: '10px', color: 'var(--text-secondary)', display: 'block' }}>
                {formatBillingPeriod()}
              </Text>
            )}
          </Space>
        )}

        {!usage.can_use && (
          <div style={{ 
            marginTop: '8px', 
            padding: '6px', 
            background: 'rgba(255, 77, 79, 0.1)', 
            borderRadius: '4px',
            border: '1px solid rgba(255, 77, 79, 0.2)'
          }}>
            <Text style={{ color: '#ff4d4f', fontSize: '10px' }}>
              Limit reached - Upgrade needed
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ComplianceUsageWidget;